import { useId, useState, type ReactNode } from 'react'
import { Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TextReveal } from '@/components/motion'

export interface FaqItem {
  question: string
  answer: string
}

/**
 * FAQ section (reference grid .5fr / .3fr gap / .25fr): a huge "FAQ"
 * heading over an accordion with "+" toggles, and an aside on the right
 * (a featured story, or links). One answer open at a time; opening animates
 * grid-template-rows 0fr → 1fr over 0.6s. Closed answers are inert.
 */
export function ServiceFaq({
  items,
  aside,
  title = 'FAQ',
  className,
}: {
  items: FaqItem[]
  aside?: ReactNode
  title?: string
  className?: string
}) {
  const [open, setOpen] = useState<number | null>(null)
  const uid = useId()

  return (
    <section className={cn('landing-section', className)}>
      <div className="landing-container grid gap-16 lg:grid-cols-[5fr_3fr_2.5fr] lg:gap-0">
        <div>
          <TextReveal as="h2" className="landing-text-hero text-[var(--color-landing-gray-900)] dark:text-white">
            {title}
          </TextReveal>
          <ul className="mt-10 border-t border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-800)] md:mt-14">
            {items.map((item, i) => {
              const isOpen = open === i
              const qId = `${uid}-q${i}`
              const aId = `${uid}-a${i}`
              return (
                <li key={item.question} className="border-b border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-800)]">
                  <h3>
                    <button
                      id={qId}
                      type="button"
                      aria-expanded={isOpen}
                      aria-controls={aId}
                      onClick={() => setOpen(isOpen ? null : i)}
                      className="group flex w-full items-center justify-between gap-6 py-6 text-left focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-landing-accent)] md:py-7"
                    >
                      <span className="font-[family-name:var(--font-landing-display)] text-base uppercase leading-[1.45] tracking-[-0.01em] text-[var(--color-landing-gray-900)] transition-colors duration-200 group-hover:text-[var(--color-landing-accent)] dark:text-white dark:group-hover:text-[#4d44ff] md:text-lg">
                        {item.question}
                      </span>
                      <Plus
                        aria-hidden
                        className={cn(
                          'h-6 w-6 shrink-0 text-[var(--color-landing-gray-900)] transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] dark:text-white',
                          isOpen && 'rotate-45'
                        )}
                      />
                    </button>
                  </h3>
                  <div
                    id={aId}
                    role="region"
                    aria-labelledby={qId}
                    inert={!isOpen}
                    className="grid transition-[grid-template-rows] duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
                    style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
                  >
                    <div className="overflow-hidden">
                      <p className="landing-text-body-lg max-w-[60ch] pb-7 text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
        {aside && <div className="lg:col-start-3">{aside}</div>}
      </div>
    </section>
  )
}

export default ServiceFaq
